import { CircleAlert, Smartphone } from 'lucide-react';
import { useId, useState } from 'react';

import { Spinner } from '@/components/auth-frame/Spinner';
import { Alert, AlertDescription } from '@/components/lumen/alert';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { Input } from '@/components/lumen/input';
import { Label } from '@/components/lumen/label';
import { useLanguage } from '@contexts/LanguageContext';

import { MFASetupDialog } from './MFASetupDialog';

interface MFAAddDeviceDialogProps {
  opened: boolean;
  onClose: () => void;
  onDeviceAdded: () => void;
  sessionToken: string;
}

/**
 * Add a second authenticator device (was MFAAddDeviceModal). The logic is the old modal's, which is
 * still a mock: no API is called, the QR is a fixed sample, and verifying always succeeds after 1 s.
 * The sign-in mounts it but nothing opens it yet (as before).
 */
export function MFAAddDeviceDialog({ opened, onClose, onDeviceAdded, sessionToken }: MFAAddDeviceDialogProps) {
  const { t } = useLanguage();
  const deviceNameId = useId();
  const userNameId = useId();
  const [deviceName, setDeviceName] = useState('');
  const [createdByName, setCreatedByName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState<{
    qrCodeUrl: string;
    secret: string;
    backupCodes: string[];
    deviceId: number;
  } | null>(null);

  const handleAddDevice = async () => {
    if (!deviceName.trim() || !createdByName.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Call API to add device
      // For now, show a notification
      // (The old log also printed the device and user names; names stay out of the console.)
      console.log('[ADD DEVICE] Adding device:', { hasSessionToken: Boolean(sessionToken) });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock response - in real implementation, call the API
      const mockResponse = {
        deviceId: 456,
        qrCodeUrl:
          'otpauth://totp/Jarvis:user@example.com?algorithm=SHA1&digits=6&issuer=Jarvis&period=30&secret=JBSWY3DPEHPK3PXP',
        secret: 'JBSWY3DPEHPK3PXP',
        backupCodes: ['12345678', '87654321', '11223344', '55667788', '99001122'],
      };

      setSetupData(mockResponse);
      setShowSetup(true);
    } catch (error) {
      console.error('[ADD DEVICE] Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyDevice = async (_code: string) => {
    try {
      // TODO: Call verify-setup API (the code is not logged: it is a live one-time code)

      // Simulate success
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setShowSetup(false);
      onDeviceAdded();
      handleClose();
    } catch (error) {
      console.error('[VERIFY DEVICE] Error:', error);
      throw error;
    }
  };

  const handleClose = () => {
    setDeviceName('');
    setCreatedByName('');
    setShowSetup(false);
    setSetupData(null);
    onClose();
  };

  if (showSetup && setupData) {
    return (
      <MFASetupDialog
        opened={showSetup}
        qrCodeUrl={setupData.qrCodeUrl}
        secret={setupData.secret}
        backupCodes={setupData.backupCodes}
        deviceId={setupData.deviceId}
        onVerify={handleVerifyDevice}
        onClose={() => setShowSetup(false)}
        isVerifying={false}
      />
    );
  }

  const canSubmit = Boolean(deviceName.trim() && createdByName.trim());

  return (
    <Dialog
      open={opened}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="gap-5 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">{t('lumen.auth.addDevice.title')}</DialogTitle>
          <DialogDescription>{t('lumen.auth.addDevice.description')}</DialogDescription>
        </DialogHeader>

        <Alert variant="info">
          <CircleAlert aria-hidden="true" />
          <AlertDescription>
            <p>{t('lumen.auth.addDevice.hint')}</p>
          </AlertDescription>
        </Alert>

        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit && !isLoading) void handleAddDevice();
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor={deviceNameId}>
              {t('lumen.auth.addDevice.deviceName')}
              <span className="text-err" aria-hidden="true">
                *
              </span>
            </Label>
            <div className="relative">
              <Smartphone
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
                aria-hidden="true"
              />
              <Input
                id={deviceNameId}
                className="pl-9"
                placeholder={t('lumen.auth.addDevice.deviceNamePlaceholder')}
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor={userNameId}>
              {t('lumen.auth.addDevice.userName')}
              <span className="text-err" aria-hidden="true">
                *
              </span>
            </Label>
            <Input
              id={userNameId}
              placeholder={t('lumen.auth.addDevice.userNamePlaceholder')}
              value={createdByName}
              onChange={(e) => setCreatedByName(e.target.value)}
              required
            />
          </div>

          <div className="mt-2 grid gap-2">
            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit || isLoading}
              aria-busy={isLoading || undefined}
            >
              {isLoading && <Spinner />}
              {t('lumen.auth.addDevice.generate')}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={handleClose} disabled={isLoading}>
              {t('lumen.auth.addDevice.cancel')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MFAAddDeviceDialog;
